import FullCalendar from '@fullcalendar/react';
import { WebPageEvents } from '@griply/types';
import { getTimeFormat } from '@griply/utils';
import { startOfDay, startOfWeek } from 'date-fns';
import { useExternalCalendarFullCalendarEvents } from 'features/external-connections';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useSortedSmartLists } from 'features/smart-lists';
import { useSortedTags } from 'features/tags';
import { useTaskTodayControls } from 'features/tasks';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarSlotContextMenu } from 'shared/components/connected/calendar-slot-context-menu';
import { FullCalendarWeek } from 'shared/components/ui/full-calendar-week';
import { weekdaysNumberMap } from 'shared/constants';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useCalendarEventControls } from 'shared/hooks/use-calendar-event-controls';
import { useCalendarTaskSync } from 'shared/hooks/use-calendar-task-sync';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes/paths';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { getScheduleByDate } from 'shared/utils/schedules';

import { useCalendarTasks } from '../../hooks/use-calendar-tasks';
import { useGoalDeadlineEvents } from '../../hooks/use-goal-deadline-events';
import { useTaskEvents } from '../../hooks/use-task-events';

const dayRange = 7;

export type WeekProps = {
  rangeReferenceDate?: Timestamp;
  scrollTime?: string;
  fullCalendarRef?: React.RefObject<FullCalendar | null>;
};

export const Week: React.FC<WeekProps> = ({
  rangeReferenceDate,
  scrollTime,
  fullCalendarRef,
}) => {
  useTrackPageVisitEvent(WebPageEvents.UpcomingWeek, undefined);
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const tags = useSortedTags();
  const smartLists = useSortedSmartLists();

  const startDate = useMemo(
    () =>
      startOfDay(
        rangeReferenceDate ??
          startOfWeek(today, {
            weekStartsOn: weekdaysNumberMap[weekStartsOn],
          }),
      ),
    [rangeReferenceDate, today, weekStartsOn],
  );
  const tasks = useCalendarTasks(startDate, dayRange);

  const filteredTasks = useMemo(
    () =>
      tasks.filter(
        (task) =>
          !task.deletedAt &&
          (task.type !== TaskType.Habit ||
            !user?.settings?.upcoming?.hideHabits),
      ),
    [tasks, user?.settings?.upcoming?.hideHabits],
  );

  const navigate = useNavigate();
  const openTaskDetail = useOpenTaskDetail();
  const { onComplete } = useTaskTodayControls();
  const taskEvents = useTaskEvents(filteredTasks, {
    rangeReferenceDate: startDate,
    dayRange,
    hideCompleted: user?.settings?.upcoming?.hideCompleted,
  });
  const goalDeadlineEvents = useGoalDeadlineEvents();
  const externalCalendarEvents = useExternalCalendarFullCalendarEvents(
    startDate,
    dayRange,
  );
  const {
    onEventChange,
    onEventReceive,
    onSelect,
    onDateTime,
    slotContextMenu,
  } = useCalendarEventControls(
    lifeAreas,
    goals,
    filteredTasks,
    tags,
    smartLists,
  );
  const { calendarSyncStates, onToggleCalendarVisibility } =
    useCalendarTaskSync(filteredTasks);

  const _openEventDetail = useCallback(
    (id: ID) => {
      const task = filteredTasks.find(({ id: taskId }) => taskId === id);
      if (task) {
        openTaskDetail(task);
        return;
      }

      const goal = goals.find(({ id: goalId }) => goalId === id);
      if (goal) {
        navigate(replaceUrlParams(Paths.GoalDetail, { id: goal.id }));
        return;
      }

      const lifeArea = lifeAreas.find(({ id: goalId }) => goalId === id);
      if (lifeArea) {
        navigate(replaceUrlParams(Paths.LifeAreaDetail, { id: lifeArea.id }));
        return;
      }
    },
    [filteredTasks, goals, lifeAreas, openTaskDetail, navigate],
  );

  const _onComplete = useCallback(
    (id: ID, date: Timestamp) => {
      const task = filteredTasks.find(({ id: taskId }) => taskId === id);
      if (!task) {
        return;
      }

      const daySchedule = getScheduleByDate(task.schedules, { date, today });

      onComplete(task, ScheduleEntryState.Complete, {
        referenceDate: date,
        toggleDayEntry: !!daySchedule?.frequency,
      });
    },
    [filteredTasks, today, onComplete],
  );

  const events = useMemo(
    () => [
      ...(user?.settings?.upcoming?.hideExternalCalendarEvents
        ? []
        : externalCalendarEvents),
      ...taskEvents,
      ...goalDeadlineEvents,
    ],
    [
      externalCalendarEvents,
      goalDeadlineEvents,
      taskEvents,
      user?.settings?.upcoming?.hideExternalCalendarEvents,
    ],
  );

  return (
    <>
      <FullCalendarWeek
        fullCalendarRef={fullCalendarRef}
        tasks={filteredTasks}
        goals={goals}
        lifeAreas={lifeAreas}
        tags={tags}
        smartLists={smartLists}
        onCompleteTask={_onComplete}
        onClickEvent={_openEventDetail}
        calendarSyncStates={calendarSyncStates}
        onToggleCalendarVisibility={onToggleCalendarVisibility}
        events={events}
        rangeReferenceDate={startDate}
        scrollTime={scrollTime}
        rangeDays={dayRange}
        eventChange={onEventChange}
        eventReceive={onEventReceive}
        dateClick={onDateTime}
        select={onSelect}
        selectConstraint={{
          startTime: '00:00',
          endTime: '23:59:59',
        }}
        timeFormat={getTimeFormat(user)}
        weekStartsOn={weekStartsOn}
        selectable
        editable
        droppable
      />

      {slotContextMenu.slot && <CalendarSlotContextMenu {...slotContextMenu} />}
    </>
  );
};
